import React, { forwardRef } from "react";
import { Section, Text, FlagDivider, Link } from "@/components";
import "./OurVision.scss";

export const ComingSoonSection = (): JSX.Element => {
  // Data for platform list to enable mapping
  const platforms = [
    { name: "iOS" },
    { name: "Android" },
    { name: "Telegram Mini App" },
  ];

  return (
    <div className="rounded-[20px] md:rounded-[60px] overflow-hidden comming-soon-section">
      <div className="relative w-full h-full max-w-[1240px] mx-auto mb-0">
        <div className="px-5 md:px-[88px] py-8 flex flex-col md:flex-row items-center justify-between">
          <div className="w-full md:w-1/2 flex justify-center items-center">
            <img
              className="hidden md:block w-auto h-[375px] sm:h-[556px] object-contain"
              alt="iPhone Pro mockup"
              src="/images/home/iphone-coming-soon.png"
              loading="lazy"
            />
          
            <img
              className="block md:hidden w-auto h-[343px] sm:h-[556px] object-contain ml-[25px] md:ml-[60px]"
              alt="iPhone Pro mockup"
              src="/images/home/iphone-coming-soon-mobile.png"
              loading="lazy"
            />
          </div>

          <div className="w-full md:w-1/2 flex justify-center md:justify-start border-none shadow-none bg-transparent">
            <div className="p-0 flex flex-col items-center md:items-start gap-[38px] sm:gap-10">
              <div className="flex flex-col">
                <Text.Heading size="md" className="text-center md:text-left text-[#000] text-[48px] md:!text-[54px] md:leading-![67.5px] font-calSans font-400">
                  Coming Soon
                </Text.Heading>
                <Text.Heading size="lg" className="text-center md:text-left text-white text-[48px] md:!text-[54px] md:!leading-[67.5px] font-calSans font-400 mobile-app-text">
                  Mobile App
                </Text.Heading>
              </div>

              <div className="max-w-[260px] md:max-w-auto mb-[30px] md:mb-0">
                <div className="w-full grid grid-cols-2 md:grid-cols-1 gap-y-5">
                  {platforms.map((platform, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center ${
                        index === platforms.length - 1 && platforms.length % 2 === 1 
                          ? "col-span-2 md:col-span-1" 
                          : ""
                      }`}
                    >
                      <div className="relative w-5 h-5 mr-3">
                        <div className="relative w-5 h-5">
                          <img
                            className="absolute w-5 h-5 top-0 left-0 text-black"
                            alt="CheckIcon icon"
                            src="/images/home/black-circle-check.svg"
                            loading="lazy"
                          />
                        </div>
                      </div>
                      <Text.Description className="font-light text-black font-semibold text-[24px] md:text-[24px] md:leading-[36px]">
                        {platform.name}
                      </Text.Description>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const LeaderShipSection = (): JSX.Element => {
  const users = [
    {
      name: "Dustin Byington",
      position: "CEO & CPO",
      avatar: "/images/avatars/dustin.png",
      linkedin: "/images/home/linkedin.svg",
      url: "https://www.linkedin.com/in/dbyington/",
      exPositions: [
        "2x 🦄",
        "Ex Goldman Sachs",
        "Ivy League Athlete"
      ]
    },
    {
      name: "Michał Michalski",
      position: "Chief Technology Officer",
      avatar: "/images/avatars/michale.png",
      linkedin: "/images/home/linkedin.svg",
      url: "https://www.linkedin.com/in/buyuk/",
      exPositions: [
        "Ex Microsoft,",
        "Samsung,",
        "and Nokia"
      ]
    }
  ];
  return (
    <>
      {/* desktop version */}
      <div className="hidden md:flex justify-between">
        <div className="max-w-[502px] md:mt-[70px]">
          <div className="flex flex-col gap-5">
            <Text.Heading size="md" className="text-white vision-title md:text-[60px] md:leading-[78px]">
              Our Vision
            </Text.Heading>
            <Text.Description size="sm" className="!text-[#A9AAB0] font-400 md:!leading-[26px]">
              In the near future every successful investor will leverage AI to amplify their capabilities. Market intelligence, due diligence, and deal screening won’t just be easier with AI—they’ll be more profitable. With the IntelliRoom™, that future is here today.  
            </Text.Description>
          </div>

          <div className="mt-6 mb-4">
            <FlagDivider />
          </div>

          <div className="flex flex-col gap-6">
            <Text.Heading size="md" className="text-white vision-title md:text-[60px] md:leading-[78px]">
              Leadership
            </Text.Heading>

            <div className="flex md:gap-10">
              {users.map((user, index) => (
                <div key={`user-${index}`}>
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-[231px] h-[231px]"
                    loading="lazy"
                  />

                  <div className="mt-4">
                    <div className="flex justify-between">
                      <div>
                        <Text.Description size="md" className="text-white font-700">
                          {user.name}
                        </Text.Description>

                        <Text.Description size="2xs" className="!text-[#35E8EF]">
                          {user.position}
                        </Text.Description>

                        {user.exPositions.map((exPosition, exIndex) => (
                          <Text.Description
                            key={`${index}-ex-position-${exIndex}`}
                            size="xs"
                          >
                            {exPosition}
                          </Text.Description>
                        ))}
                      </div>

                      <div className="mt-0.5">
                        <Link
                          to={user.url}
                          target="_blank"
                          className="!p-0 linkedin-link"
                        >
                          <img
                            src={user.linkedin}
                            alt="linkedin"
                            className="w-6 h-6"
                            loading="lazy"
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* head image */}
        <div className="max-w-[612px]">
          <img src="/images/home/leadership.png" alt="Leadership" className="w-full h-auto object-cover mt-[70px]" loading="lazy" />
        </div>
      </div>

      {/* mobile version */}
      <div className="flex md:hidden justify-between">
        <div className="w-full mt-2.5">
          <div className="flex flex-col items-center gap-2">
            <Text.Heading size="md" className="text-white vision-title text-[48px]">
              Our Vision
            </Text.Heading>
            <Text.Description size="sm" className="!text-[#C7C7C7] text-center font-400 !leading-[20px]">
              In the near future every successful investor will leverage AI to amplify their capabilities. Market intelligence, due diligence, and deal screening won’t just be easier with AI—they’ll be more profitable. With the IntelliRoom™, that future is here today.  
            </Text.Description>

            {/* head image */}
              <div className="w-full mt-10">
                <img src="/images/home/leadership.png" alt="Leadership" className="w-full h-auto object-cover" loading="lazy" />
              </div>
          </div>

          <div className="w-full flex flex-col items-center gap-6 mt-10">
            <Text.Heading size="md" className="text-white vision-title text-[48px]">
              Leadership
            </Text.Heading>

            <div className="w-full flex flex-col gap-[50px] md:gap-5 mx-4">
              {users.map((user, index) => (
                <div key={`user-${index}`}>
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full"
                    loading="lazy"
                  />

                  <div className="mt-4">
                    <div className="flex justify-between">
                      <div className="">
                        <Text.Description size="md" className="text-white font-700 !text-[30px] mb-[12px]">
                          {user.name}
                        </Text.Description>

                        <Text.Description size="2xs" className="!text-[#35E8EF] !text-[22px] mb-[18px]">
                          {user.position}
                        </Text.Description>

                        {user.exPositions.map((exPosition, exIndex) => (
                          <Text.Description
                            key={`${index}-ex-position-${exIndex}`}
                            size="xs"
                            className="!text-[22px] mb-1"
                          >
                            {exPosition}
                          </Text.Description>
                        ))}
                      </div>

                      <Link
                        to={user.url}
                        target="_blank"
                        className="!p-0"
                      >
                        <img
                          src={user.linkedin}
                          alt="linkedin"
                          className="w-[35px] h-[35px]"
                          loading="lazy"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export const OurVisionSection = forwardRef<HTMLElement, {}>((props, ref) => {
  return (
    <Section
      ref={ref}
      id="our-vision-section"
      className="pb-0 md:pb-0 px-[30px] md:px-[44px] pb-[38px] md:py-[30px] bg-black rounded-t-[20px] md:rounded-t-[60px]"
    >
      <div className="max-w-[1292px]">
        {/* our vision section */}
        <LeaderShipSection />

        <div className="mt-[114px]">
          {/* comming soon section */}
          <ComingSoonSection />
        </div>

        <div className="mt-[44px] md:mt-[54px]">
          <FlagDivider />
        </div>
      </div>
    </Section>
  );
});
